import { readonly, ref, watch } from 'vue';
import { createFetch } from '@vueuse/core';
import dayjs from 'dayjs';
import environment from '~/services/env';

import type { Dayjs } from 'dayjs';

interface StatusResponse {
  status: 'ok';
  maintenance: boolean;
  timestamp: string;
}

export interface UseApiStatus {
  statusOk: Readonly<typeof statusOk>;
  lastStatusCheck: Readonly<typeof lastStatusCheck>;
  maintenanceModeActive: Readonly<typeof maintenanceModeActive>;
  init: () => Promise<void>;
  checkApiStatus: () => Promise<void>;
}

const initialized = ref<boolean>(false);
const checkInterval = ref<NodeJS.Timer>();
const statusOk = ref<boolean>(true);
const lastStatusCheck = ref<Dayjs | null>(null);
const maintenanceModeActive = ref<boolean>(false);

const useApiStatus = (): UseApiStatus => {
  const useStatusFetch = createFetch({
    baseUrl: environment('VITE_API_URL'),
    options: {
      immediate: true,
      refetch: false,
    },
  });

  const checkApiStatus = async (): Promise<void> => {
    const response = await useStatusFetch('/api/status').json<StatusResponse>();

    if (response.statusCode.value !== 200 || !response.data.value) {
      statusOk.value = false;

      return;
    }

    statusOk.value = true;
    lastStatusCheck.value = dayjs(response.data.value.timestamp);
    maintenanceModeActive.value = response.data.value.maintenance;
  };

  const init = async (): Promise<void> => {
    if (initialized.value) {
      return;
    }

    watch(() => maintenanceModeActive.value, (newValue, oldValue) => {
      if (!newValue && oldValue) {
        window.location.reload();
      }
    });

    await checkApiStatus();

    checkInterval.value = setInterval(async () => {
      await checkApiStatus();
    }, 5000);

    initialized.value = true;
  };

  return {
    statusOk: readonly(statusOk),
    lastStatusCheck: readonly(lastStatusCheck),
    maintenanceModeActive: readonly(maintenanceModeActive),

    init,
    checkApiStatus,
  };
};

export default useApiStatus;